import React, { useState, useEffect } from 'react';
import { 
  Megaphone, Plus, Edit2, Trash2, Pin, PinOff, Eye, Calendar,
  Globe, Users, ChevronDown, Search, AlertCircle, CheckCircle, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';

const AdminAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [search, setSearch] = useState('');
  
  const { api } = useAdminAuth();

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    announcement_type: 'general',
    visibility: 'all',
    visible_to_tiers: [],
    visible_to_countries: [],
    is_pinned: false,
    expires_at: '',
    link_url: '',
    link_text: ''
  });

  const announcementTypes = [
    { value: 'general', label: 'General', color: 'bg-blue-100 text-blue-700', icon: Info },
    { value: 'important', label: 'Important', color: 'bg-red-100 text-red-700', icon: AlertCircle },
    { value: 'event', label: 'Event', color: 'bg-purple-100 text-purple-700', icon: Calendar },
    { value: 'maintenance', label: 'Maintenance', color: 'bg-orange-100 text-orange-700', icon: AlertCircle }
  ];

  const tiers = ['Silver', 'Gold', 'Platinum', 'Rhodium', 'Titanium', 'Founder'];

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/announcements/?include_inactive=true');
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Title and content are required');
      return;
    }

    try {
      const payload = {
        ...formData,
        expires_at: formData.expires_at || null,
        visible_to_tiers: formData.visibility === 'tier_specific' ? formData.visible_to_tiers : null,
        visible_to_countries: formData.visibility === 'country_specific' ? formData.visible_to_countries : null
      };

      if (editingAnnouncement) {
        await api.put(`/api/admin/announcements/${editingAnnouncement.announcement_id}`, payload);
        toast.success('Announcement updated');
      } else {
        await api.post('/api/admin/announcements/', payload);
        toast.success('Announcement created');
      }
      
      setShowDialog(false);
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to save announcement'));
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      announcement_type: announcement.announcement_type || 'general',
      visibility: announcement.visibility || 'all',
      visible_to_tiers: announcement.visible_to_tiers || [],
      visible_to_countries: announcement.visible_to_countries || [],
      is_pinned: announcement.is_pinned || false,
      expires_at: announcement.expires_at ? announcement.expires_at.slice(0, 16) : '',
      link_url: announcement.link_url || '',
      link_text: announcement.link_text || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    
    try {
      await api.delete(`/api/admin/announcements/${announcementId}`);
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleTogglePin = async (announcement) => {
    try {
      await api.post(`/api/admin/announcements/${announcement.announcement_id}/pin`);
      toast.success(announcement.is_pinned ? 'Unpinned' : 'Pinned');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to toggle pin');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      announcement_type: 'general',
      visibility: 'all',
      visible_to_tiers: [],
      visible_to_countries: [],
      is_pinned: false,
      expires_at: '',
      link_url: '',
      link_text: ''
    });
    setEditingAnnouncement(null);
  };

  const getTypeConfig = (type) => {
    return announcementTypes.find(t => t.value === type) || announcementTypes[0];
  };

  const filteredAnnouncements = announcements.filter(a => {
    if (!search) return true;
    return a.title.toLowerCase().includes(search.toLowerCase()) ||
           a.content.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="space-y-6" data-testid="admin-announcements">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Announcements</h1>
          <p className="text-gray-500">Manage announcements visible to members</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowDialog(true); }}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus size={16} className="mr-2" />
          New Announcement
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <p className="text-blue-100 text-sm">Total Active</p>
            <p className="text-3xl font-bold">{announcements.filter(a => a.is_active).length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Pinned</p>
            <p className="text-2xl font-bold">{announcements.filter(a => a.is_pinned).length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Important</p>
            <p className="text-2xl font-bold text-red-600">
              {announcements.filter(a => a.announcement_type === 'important').length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total Views</p>
            <p className="text-2xl font-bold">
              {announcements.reduce((sum, a) => sum + (a.views_count || 0), 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search announcements..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Announcements List */}
      <div className="space-y-4">
        {loading ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">Loading...</CardContent>
          </Card>
        ) : filteredAnnouncements.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Megaphone size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No announcements yet</p>
            </CardContent>
          </Card>
        ) : (
          filteredAnnouncements.map((announcement) => {
            const typeConfig = getTypeConfig(announcement.announcement_type);
            const TypeIcon = typeConfig.icon;
            
            return (
              <Card key={announcement.announcement_id} className={`${!announcement.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        {announcement.is_pinned && (
                          <Pin size={14} className="text-amber-500" />
                        )}
                        <Badge className={typeConfig.color}>
                          <TypeIcon size={12} className="mr-1" />
                          {typeConfig.label}
                        </Badge>
                        {announcement.visibility === 'tier_specific' && (
                          <Badge variant="outline" className="text-purple-600">
                            <Users size={12} className="mr-1" />
                            Tier Specific
                          </Badge>
                        )}
                        {!announcement.is_active && (
                          <Badge variant="outline" className="text-gray-500">Inactive</Badge>
                        )}
                      </div>
                      
                      <h3 className="font-semibold text-lg text-gray-900">{announcement.title}</h3>
                      <p className="text-gray-600 mt-1 line-clamp-2">{announcement.content}</p>
                      
                      <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
                        <span>Created: {new Date(announcement.created_at).toLocaleDateString()}</span>
                        <span className="flex items-center">
                          <Eye size={12} className="mr-1" />
                          {announcement.views_count || 0} views
                        </span>
                        {announcement.expires_at && (
                          <span className="text-orange-600">
                            Expires: {new Date(announcement.expires_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTogglePin(announcement)}
                        title={announcement.is_pinned ? 'Unpin' : 'Pin'}
                      >
                        {announcement.is_pinned ? <PinOff size={16} /> : <Pin size={16} />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(announcement)}
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                        onClick={() => handleDelete(announcement.announcement_id)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Announcement title"
              />
            </div>
            
            <div>
              <Label>Content *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Announcement content..."
                rows={5}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <select
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.announcement_type}
                  onChange={(e) => setFormData({ ...formData, announcement_type: e.target.value })}
                >
                  {announcementTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Visibility</Label>
                <select
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.visibility}
                  onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                >
                  <option value="all">All Members</option>
                  <option value="tier_specific">Specific Tiers</option>
                  <option value="country_specific">Specific Countries</option>
                </select>
              </div>
            </div>
            
            {formData.visibility === 'tier_specific' && (
              <div>
                <Label>Visible to Tiers</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {tiers.map(tier => (
                    <button
                      key={tier}
                      type="button"
                      onClick={() => {
                        const current = formData.visible_to_tiers || [];
                        const updated = current.includes(tier)
                          ? current.filter(t => t !== tier)
                          : [...current, tier];
                        setFormData({ ...formData, visible_to_tiers: updated });
                      }}
                      className={`px-3 py-1 rounded-full text-sm border ${
                        (formData.visible_to_tiers || []).includes(tier)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      {tier}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Expires At (Optional)</Label>
                <Input
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                />
              </div>
              
              <div className="flex items-center space-x-2 pt-6">
                <input
                  type="checkbox"
                  id="is_pinned"
                  checked={formData.is_pinned}
                  onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_pinned" className="cursor-pointer">Pin this announcement</Label>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Link URL (Optional)</Label>
                <Input
                  value={formData.link_url}
                  onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>Link Text</Label>
                <Input
                  value={formData.link_text}
                  onChange={(e) => setFormData({ ...formData, link_text: e.target.value })}
                  placeholder="Learn More"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white">
                {editingAnnouncement ? 'Update' : 'Create'} Announcement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAnnouncements;
